import React, { useMemo, useState } from "react";
import Swal from "sweetalert2";
import DataTableComponentFrontPagination from "../../common/DataTableComponentFrontPagination";
import TextInput from "../../library/TextInput";
import NumberInput from "../../library/NumberInput";
import Button from "../../library/Button";
import PositiveButton from "../../library/PositiveButton";
import ButtonGroup from "../../library/ButtonGroup";
import CommonButton from "../../common/Button/Button";
import { DeleteFirstAidCatalogItem, SaveFirstAidCatalogItem } from "../../../services/FirstAidBoxService";
import { invalidateFirstAidBoxQueries, useFirstAidCatalog } from "../../../query/hooks/QueryHooksFirstAidBox";
import { getOhsErrorMessage } from "./ohsUtils";
import { FormGrid, SectionCard, SectionHeader, SectionTitle } from "./Ohs.styles";

const EMPTY_ITEM = { id: null, itemName: "", category: "", expectedQuantity: 1, unit: "each" };
const EXPORT_HEADER = ["Item", "Category", "Expected Quantity", "Unit"];

/**
 * Item catalog - the list every box is checked against, in check-form order (new items go last).
 */
function ItemCatalog() {
    const { data: catalog, isLoading } = useFirstAidCatalog();
    const [search, setSearch] = useState("");
    const [editingItem, setEditingItem] = useState(null);
    const [itemNameError, setItemNameError] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    const filteredCatalog = useMemo(() => {
        const searchText = search.trim().toLowerCase();
        if (!searchText) {
            return catalog;
        }
        return catalog.filter((catalogItem) => {
            return catalogItem.item_name.toLowerCase().includes(searchText)
                || (catalogItem.category || "").toLowerCase().includes(searchText);
        });
    }, [catalog, search]);

    const setEditingField = (fieldName, value) => {
        setEditingItem((currentItem) => ({ ...currentItem, [fieldName]: value }));
    };

    const handleSave = async () => {
        if (!editingItem.itemName.trim()) {
            setItemNameError("Item name is required");
            return;
        }

        setIsSaving(true);
        try {
            await SaveFirstAidCatalogItem({
                id: editingItem.id || undefined,
                itemName: editingItem.itemName.trim(),
                category: editingItem.category.trim() || null,
                expectedQuantity: Math.max(1, Number(editingItem.expectedQuantity) || 1),
                unit: editingItem.unit.trim() || "each",
            });
            await invalidateFirstAidBoxQueries();
            setEditingItem(null);
        } catch (error) {
            console.log("ItemCatalog.js->handleSave->Error", error);
            Swal.fire("Error", getOhsErrorMessage(error, "The item could not be saved."), "error");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (catalogItem) => {
        try {
            const confirmation = await Swal.fire({
                title: "Are you sure?",
                text: `${catalogItem.item_name} will be removed from the catalog and the check form. Past checks of it are kept.`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "var(--button-color)",
                cancelButtonColor: "var(--states-alert)",
                confirmButtonText: "Yes, delete it!",
            });
            if (!confirmation.isConfirmed) {
                return;
            }

            await DeleteFirstAidCatalogItem(catalogItem.id);
            await invalidateFirstAidBoxQueries();
            Swal.fire("Deleted!", "The item has been deleted.", "success");
        } catch (error) {
            console.log("ItemCatalog.js->handleDelete->Error", error);
            Swal.fire("Error", "The item could not be deleted.", "error");
        }
    };

    const columns = [
        {
            name: "Item",
            selector: "item_name",
            sortable: true,
            grow: 3,
            minWidth: "240px",
            cell: (row) => <span className="textLimit100" title={row.item_name}>{row.item_name}</span>,
        },
        {
            name: "Category",
            selector: "category",
            sortable: true,
            grow: 2,
            minWidth: "160px",
            cell: (row) => row.category || "-",
        },
        {
            name: "Expected",
            selector: "expected_quantity",
            sortable: true,
            minWidth: "120px",
            cell: (row) => `${row.expected_quantity} ${row.unit}`,
        },
        {
            name: "Action",
            width: "110px",
            right: true,
            cell: (row) => (
                <div className="assessment-08 btn-dropdown-grp">
                    <div className="as-buttons">
                        <button
                            type="button"
                            className="btn btn-primary rounded-circle"
                            title="Edit"
                            onClick={() => setEditingItem({
                                id: row.id,
                                itemName: row.item_name,
                                category: row.category || "",
                                expectedQuantity: row.expected_quantity,
                                unit: row.unit || "each",
                            })}
                        >
                            <i className="fal fa-edit"></i>
                        </button>
                        <button
                            type="button"
                            className="btn btn-danger rounded-circle"
                            title="Delete"
                            onClick={() => handleDelete(row)}
                        >
                            <i className="fal fa-trash-alt"></i>
                        </button>
                    </div>
                </div>
            ),
        },
    ];

    /**
     * Rows for the CSV/XLSX/PDF export, in catalog order.
     * @returns {Object} { header, data, pdfData }
     */
    const exportData = () => {
        const exportRows = filteredCatalog.map((catalogItem) => [
            catalogItem.item_name,
            catalogItem.category || "",
            catalogItem.expected_quantity,
            catalogItem.unit,
        ]);

        return {
            header: EXPORT_HEADER,
            data: exportRows.map((exportRow) => Object.fromEntries(EXPORT_HEADER.map((columnName, columnIndex) => [columnName, exportRow[columnIndex]]))),
            pdfData: exportRows,
        };
    };

    return (
        <>
            {editingItem && (
                <SectionCard>
                    <SectionHeader>
                        <SectionTitle>{editingItem.id ? "Edit Item" : "Add Item"}</SectionTitle>
                    </SectionHeader>
                    <FormGrid>
                        <TextInput
                            id="ohsCatalogItemName"
                            label="Item Name"
                            required
                            maxLength={150}
                            value={editingItem.itemName}
                            onChange={(event) => {
                                setEditingField("itemName", event.target.value);
                                setItemNameError("");
                            }}
                            error={Boolean(itemNameError)}
                            errorText={itemNameError}
                        />
                        <TextInput
                            id="ohsCatalogCategory"
                            label="Category"
                            placeholder="e.g. Bandages"
                            maxLength={80}
                            value={editingItem.category}
                            onChange={(event) => setEditingField("category", event.target.value)}
                            helperText="Groups items on the check form."
                        />
                        <NumberInput
                            label="Expected Quantity"
                            min={1}
                            value={editingItem.expectedQuantity}
                            onChange={(expectedQuantity) => setEditingField("expectedQuantity", expectedQuantity)}
                        />
                        <TextInput
                            id="ohsCatalogUnit"
                            label="Unit"
                            placeholder="e.g. each, pair, roll"
                            maxLength={30}
                            value={editingItem.unit}
                            onChange={(event) => setEditingField("unit", event.target.value)}
                        />
                    </FormGrid>
                    <ButtonGroup>
                        <PositiveButton onClick={handleSave} disabled={isSaving} />
                        <CommonButton type="button" variant="danger" onClick={() => setEditingItem(null)} iconLeft={<i className="fal fa-times"></i>}>
                            Cancel
                        </CommonButton>
                    </ButtonGroup>
                </SectionCard>
            )}

            <DataTableComponentFrontPagination
                data={filteredCatalog}
                columns={columns}
                loading={isLoading}
                keyField="id"
                search={search}
                setSearch={setSearch}
                exportData={exportData}
                exportFileName="First_Aid_Item_Catalog"
                tableButton={[
                    <ButtonGroup key="catalog-actions">
                        <Button onClick={() => setEditingItem(EMPTY_ITEM)} disabled={Boolean(editingItem)} title="Add item">
                            <i className="fal fa-plus"></i>
                            Add Item
                        </Button>
                    </ButtonGroup>,
                ]}
            />
        </>
    );
}

export default ItemCatalog;
